import { Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { PowerForecast } from "app/models/monitor/power-forecast.model";
import { HelperService } from "app/shared/services/helper-service/helper.service";
import { EThemeName, ThemeService } from '@wind-log_library/fe-component-library';
import { ProvidersColorsService } from "app/shared/services/theme-colors/providers-colors.service";
import moment from 'moment';
import { takeUntil } from "rxjs/operators";
import { ReplaySubject } from "rxjs";
import { RatedPower } from "app/models/shared/rated-power";
import { DateTimeFormatPreferencePipe } from "app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe";
import { TimezoneService } from "app/shared/services/timezone/timezone.service";
import Highcharts from "highcharts";
import { getXDateFormat } from "app/shared/helper/highcharts.helper";
import { DateTimeFormatService } from "app/shared/services/date-time-format/date-time-format.service";
import { CS_AREA_CHART_OPTIONS, CS_LINE_CHART_OPTIONS } from "app/shared/helper/highchart-options";

@Component({
    selector: 'app-power-forecast-chart',
    templateUrl: './power-forecast-chart.component.html',
    styleUrls: ['./power-forecast-chart.component.scss'],
    standalone: false
})
export class PowerForecastChartComponent implements OnChanges, OnInit, OnDestroy {

  @Input() powerForecast: PowerForecast;
  @Input() page: string;
  
  public timezone: string;
  public yAxisPowerMax = 120;
  public yAxisWindspeedMax: number;
  public chartDatasets: Array<any>;
  public timestamps = [];
  private unsubscriber = new ReplaySubject();

  public chartOptions: Highcharts.Options;
  public highcharts = Highcharts;
  public chartRef: Highcharts.Chart | null = null;
  private isDarkTheme = false;

  get isWindspeedAvailable(): boolean {
    return !!this.powerForecast?.dataSeries?.windSpd;
  }

  constructor(
    private themeColors: ProvidersColorsService,
    private themeService: ThemeService,
    private helper: HelperService,
    private translate: TranslateService,
    private dateTimeFormatPreferencePipe: DateTimeFormatPreferencePipe,
    private dateTimeFormatService: DateTimeFormatService,
    private timezoneService: TimezoneService
  ) { }

  public ngOnInit() {
    this.subscribeTranslateService();
    this.themeService.getTheme().pipe(takeUntil(this.unsubscriber)).subscribe((theme) => {
      this.isDarkTheme = theme === EThemeName.DARK;
      this.createChart();
    });
    this.timezoneService.getTimezoneObservable().pipe(takeUntil(this.unsubscriber))
    .subscribe((tz) => { 
      this.timezone = tz.timezone;
      this.createChart();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.powerForecast) {
      this.timezone = this.timezoneService.selectedOption;
      this.createChart();
    }
  }

  public ngOnDestroy() {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  public createChart() {
    this.setStepAxis(this.powerForecast);
    this.setChartData(this.powerForecast?.dataSeries);
  }

  public getThemeColor(color: string): string {
    return this.themeService.getColor(color);
  }

  private setStepAxis(powerForecast: PowerForecast) {
    const powerMax = powerForecast?.dataSeries?.theoPower?.[0]?.data
      .map((f: any) => Math.round(f * 0.001 * 10) / 10)
      .reduce((a, b) => Math.max(a, b), -Infinity);
    const calcPowerAxisObject = this.helper.calcStepsAxis(Math.max(powerMax, powerForecast?.WTUR1_MW_rated) * 1.1);
    this.yAxisPowerMax = calcPowerAxisObject.yAxisMax || 1;

    const windspeedMax = powerForecast?.dataSeries?.windSpd?.[0]?.data.reduce((a, b) => Math.max(a, b), -Infinity);
    const calcWindspeedAxisObject = this.helper.calcStepsAxis(windspeedMax * 1.1);
    this.yAxisWindspeedMax = calcWindspeedAxisObject.yAxisMax || 1;
  }

  private setChartData(dataSeries: any) {
    if (dataSeries) {
      this.chartDatasets = dataSeries?.theoPower.map(t => ({
        label: t.provider ? (this.translate.instant('POWER_FORECAST.FORECAST_SELECT.' + (t.provider ? t.provider.charAt(0).toUpperCase() + t.provider.slice(1) : '').replaceAll(' ', '_').toUpperCase()) + ' ' + this.translate.instant('POWER_FORECAST.THEORETICAL')) : this.translate.instant('POWER_FORECAST.THEORETICAL'),
        data: t.data ? t.data.map((f: any) => Math.round(f * 0.001 * 10) / 10) : [],
        backgroundColor: dataSeries?.theoPower?.length > 1 ? 'transparent' : this.getThemeColor('--primary-background') + '33',
        borderColor: t.provider ? this.themeColors.getForecastProviderBorderColor(t.provider) : 'transparent',
        borderWidth: 2,
        pointBackgroundColor: dataSeries?.theoPower?.length > 1 ? 'transparent' : this.getThemeColor('--primary-background') + '33',
        pointBorderColor: t.provider ? this.themeColors.getForecastProviderBorderColor(t.provider) : 'transparent',
        pointRadius: 0,
        pointHoverBackgroundColor: t.provider ? this.themeColors.getForecastProviderBorderColor(t.provider) : 'transparent',
        pointHoverBorderColor: t.provider ? this.themeColors.getForecastProviderBorderColor(t.provider) : 'transparent',
      }));

      dataSeries?.windSpd?.map(t => {
        this.chartDatasets.push({
          label: this.translate.instant('PERFORMANCE_MONITORING.WIND_SPEED'),
          data: t.data ? t.data : [],
          backgroundColor: 'transparent',
          borderColor: this.themeService.getColor('--budget-follow-up-delivered-scada'),
          borderWidth: 2,
          pointBackgroundColor: 'transparent',
          pointBorderColor: this.themeService.getColor('--budget-follow-up-delivered-scada'),
          pointRadius: 0,
          pointHoverBackgroundColor: this.themeService.getColor('--budget-follow-up-delivered-scada'),
          pointHoverBorderColor: this.themeService.getColor('--budget-follow-up-delivered-scada'),
          yAxisID: 'windspeed'
        })
      });

      this.chartDatasets.push({
        label: this.translate.instant('POWER_FORECAST.NON_WORKING'),
        data: []
      });

      if (dataSeries.ratedPower) {
        this.chartDatasets.push({
          ...RatedPower.getRatedChartLine(),
          ...RatedPower.getRatedChartColor(this.themeService),
          ...{
            label: this.translate.instant('COMMON.CHART_LEGEND_RATED_POWER'),
            isRatedPower: true,
            data: dataSeries.ratedPower.data
          }
        });
      }

      this.timestamps = dataSeries?.theoPower[0]?.data
        ? dataSeries.theoPower[0].data.map((f: any, i) => (
          dataSeries.theoPower[0].startingTS + (i * dataSeries.theoPower[0].intervalTS)
        ))
        : [];
    }
    this.setChartOptions();
  }

  public chartCallback: Highcharts.ChartCallbackFunction = (chart): void => {
    this.chartRef = chart;
  };

  private setChartOptions() {
    if (!this.chartDatasets) return;
    const getPlotBands = this.getPlotBands;
    const series = this.chartDatasets.map(a => ({
      type: 'area',
      name: a.label,
      color: a.borderColor,
      lineWidth: a.isRatedPower ? 1.5 : 2,
      fillColor: a.backgroundColor,
      dashStyle: a.isRatedPower ? 'Dash' : 'Solid',
      yAxis: a.yAxisID === 'windspeed' ? 1 : 0,
      units: a.yAxisID === 'windspeed' ? 'm/s' : 'MW',
      showInLegend: true,
      showInTooltip: !a.isRatedPower,
      visible: true,
      isNonWorkingSeries: !a.borderColor && !a.backgroundColor && a.data.length === 0,
      data: a.data.map((v, i) => [this.timezoneService.unApplyTzFromDate(this.timestamps[i]).valueOf(), v])
    }));
    const dateTimeFormatPreferencePipe = this.dateTimeFormatPreferencePipe;
    this.chartOptions = Highcharts.merge(CS_AREA_CHART_OPTIONS, {
      chart: {
        height: 200
      },
      plotOptions: {
        series: {
          animation: { duration: 0 },
          marker: {
            enabled: true,
            radius: 0.1,
            symbol: 'circle',
            states: {
              hover: {
                enabled: true,
                radius: 5
              }
            }
          },
          states: {
            hover: {
              enabled: true
            },
            inactive: { enabled: false }
          },
          events: {
            legendItemClick: function (e) {
              const series = this;
              if (series.userOptions.isNonWorkingSeries) {
                const chart = series.chart;
                const xAxis = chart.xAxis[0];
                const nonWorkingBands = xAxis.plotLinesAndBands.filter(b => b.id && b.id.startsWith('nonWorkingPlot-'));
                if (series.visible) {
                  const bands = getPlotBands();
                  bands.forEach(band => xAxis.addPlotBand(band));
                } else {
                  nonWorkingBands.forEach(band => band.destroy());
                }
                return false;
              }
            }
          }
        },
      },
      legend: { 
        align: 'center',
        verticalAlign: 'top',
        layout: 'horizontal',
        padding: 5,
        itemStyle: {
          color: 'var(--font)',
          fontSize: '12px'
        },
        zIndex: 1,
        enabled: true,
        useHTML: true,
        symbolWidth: 0,
        symbolHeight: 0,
        labelFormatter: function () {
          const params = this as any;
          const seriesColor = params.userOptions.lineColor || params.userOptions.color;
          const backgroundColor = params.userOptions.fillColor;
          const seriesName = params.name;
          if (params.userOptions.isNonWorkingSeries) {
            const patternBox = `
              <span style="display: inline-flex; align-items: center; margin-right: 6px;">
                <svg width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <pattern id="legendPattern" patternUnits="userSpaceOnUse" width="4" height="4">
                      <rect width="6" height="6" fill="var(--card)"/>
                      <path d="M 0 0 L 6 6 M 5 -1 L 7 1"
                            stroke="var(--divider)"
                            stroke-width="1"/>
                    </pattern>
                  </defs>

                  <rect width="14" height="14"
                        fill="url(#legendPattern)"
                        stroke="var(--divider)"
                        stroke-width="1"/>
                </svg>
              </span>
            `;
            return `
              <div style="display: flex; align-items: center">
                ${patternBox}
                ${seriesName}
              </div>
            `;
          } else {
            const markerHtml = `<span style="display: inline-block;
              width: 14px; height: 14px;
              border: 2px solid ${seriesColor};
              border-radius: 50%;
              background-color: ${backgroundColor};
              margin-right: 5px;
              vertical-align: middle;"></span>`;
            return `<div style="display: flex; align-items: center">${markerHtml} ${seriesName}</div>`;
          }
        }
      },
      xAxis: {
        type: 'datetime',
        // tickInterval: 1000 * 60 * 60 * (this.chartDatasets.find(a => a.label === '').data.length > 100 ? 8 : 3),
        tickPixelInterval: 60,
        plotBands: this.getPlotBands(),
        minPadding: 0,
        maxPadding: 0,
        tickColor: 'var(--divider)',
        lineWidth: 1,
        lineColor: 'var(--divider)',
        gridLineWidth: 1,
        gridLineColor: 'var(--divider)',
        dateTimeLabelFormats: {
          day: getXDateFormat('dm', this.dateTimeFormatService),
          hour: getXDateFormat('t', this.dateTimeFormatService),
          minute: getXDateFormat('t', this.dateTimeFormatService)
        }
      },
      yAxis: [
        {
          tickAmount: 6,
          lineWidth: 1,
          opposite: false,
          tickColor: 'var(--font)',
          gridLineColor: 'var(--divider)',
          lineColor: 'var(--divider)',
          min: 0,
          max: this.yAxisPowerMax,
          title: {
            text: 'MW',
            style: {
              color: 'var(--font)',
              fontFamily: 'SF Display',
              fontSize: '12px'
            },
          },
          labels: {
            style: {
              color: 'var(--font)',
              fontFamily: 'SF Display',
              fontSize: '12px'
            },
            formatter: function () {
              return this.value;
            }
          }
        },
        this.page === 'park-monitor' ? {
          tickAmount: 6,
          lineWidth: 1,
          opposite: true,
          tickColor: 'var(--font)',
          gridLineColor: 'var(--divider)',
          lineColor: 'var(--divider)',
          min: 0,
          max: this.yAxisWindspeedMax,
          title: {
            text: 'm/s',
            style: {
              color: 'var(--font)',
              fontFamily: 'SF Display',
              fontSize: '12px'
            },
          },
          labels: {
            style: {
              color: 'var(--font)',
              fontFamily: 'SF Display',
              fontSize: '12px'
            },
            formatter: function () {
              return this.value;
            }
          }
        } : null
      ].filter(a => a),
      tooltip: {
        enabled: true,
        outside: true,
        followPointer: true,
        hideDelay: 0,
        shared: true,
        animation: { duration: 0 },
        useHTML: true,
        className: 'custom-shadow',
        backgroundColor: 'var(--card)',
        borderWidth: this.isDarkTheme ? 1 : 0,
        borderColor: '#495057',
        style: {
          color: 'var(--primary)'
        },
        formatter: function() {
          const date = dateTimeFormatPreferencePipe.transform(moment(this.points[0].x).toDate(), 'dmyt', this.timezone);
          let tooltipContent = `<div class="font-weight-bold" style="margin-bottom: 5px; font-size: 14px">${date}</div>`;
          this.points.filter(p => p?.series?.userOptions?.showInTooltip).forEach(p => {
            tooltipContent += `
              <div style="margin-bottom: 2px; font-size: 12px">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50px; border: 2px solid ${p?.color}; background-color: ${p.color}"></span>
                ${p?.series?.userOptions?.name}: <b>${p.y.toFixed(2)} ${p?.series?.userOptions?.units}</b>
              </div>
            `
          })
          return tooltipContent;
        }
      },
      series: series
    });
  }

  private timestampIsNonWorking(timestamp: number): boolean {
    const dt = moment(timestamp);
    const hour = dt.hours();
    const day = dt.day();
    const isWeekend = (day === 6) || (day === 0);
    return isWeekend || (hour < 8) || (hour > 17);
  }

  private getPlotBands = (): Highcharts.XAxisPlotBandsOptions[] => {
    const timeInterval = 1000 * 60 * 60;
    const allTs = this.timestamps.map(t => this.timezoneService.unApplyTzFromDate(t).valueOf());
    const timestamps = [allTs[0]];
    for(let i = 0; i < allTs.length -1 ; i++) {
      if((this.timestampIsNonWorking(allTs[i]) && !this.timestampIsNonWorking(allTs[i + 1])) || (this.timestampIsNonWorking(allTs[i + 1]) && !this.timestampIsNonWorking(allTs[i])) ) {
        timestamps.push(allTs[i]);
      }
    }
    return timestamps.map((ts, index) => (!this.timestampIsNonWorking(ts + timeInterval) ? null : {
      id: `nonWorkingPlot-${ts}`,
      from: ts,
      to: timestamps[index + 1] || allTs[allTs.length -1],
      color: {
        pattern: {
          color:'var(--divider)',
          path: 'M 0 0 L 10 10 M 9 -1 L 11 1',
          width: 10,
          height: 10
        }
      }
    })).filter(band => !!band);
  }

  private subscribeTranslateService() {
    this.translate.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe(d => {
      this.createChart();
    });
  }
}
